import React, { useState } from 'react';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { Badge } from './badge';
import { X } from 'lucide-react';

interface TagInputProps {
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

export const TagInput: React.FC<TagInputProps> = ({
  tags,
  onTagsChange,
  label = "Tags",
  placeholder = "Add tag",
  disabled = false
}) => {
  const [newTag, setNewTag] = useState('');

  const handleAddTag = () => {
    const trimmedTag = newTag.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      onTagsChange([...tags, trimmedTag]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    onTagsChange(tags.filter(tag => tag !== tagToRemove));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      
      {!disabled && (
        <div className="flex gap-2">
          <Input
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            placeholder={placeholder}
            onKeyPress={handleKeyPress}
            disabled={disabled}
          />
          <Button 
            type="button" 
            onClick={handleAddTag} 
            variant="outline"
            disabled={disabled || !newTag.trim()}
          >
            Add
          </Button>
        </div>
      )}
      
      <div className="flex flex-wrap gap-1">
        {tags.map(tag => (
          <Badge 
            key={tag} 
            variant="secondary" 
            className={`${!disabled ? 'cursor-pointer hover:bg-secondary/80' : ''}`}
            onClick={() => !disabled && handleRemoveTag(tag)}
          >
            {tag}
            {!disabled && <X className="h-3 w-3 ml-1" />}
          </Badge>
        ))}
        {tags.length === 0 && (
          <span className="text-sm text-muted-foreground">No tags added</span>
        )}
      </div>
    </div>
  );
};
